import type { Statement } from '../parser/ast.js';

/**
 * Δεν επιτρέπεται η ΙΔΙΑ μεταβλητή-μετρητής σε εμφωλευμένους βρόχους
 * (ΓΙΑ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ, σε οποιονδήποτε συνδυασμό) — ο εσωτερικός θα
 * χαλούσε τον μετρητή του εξωτερικού (π.χ. εσωτερικό ΟΣΟ με συνθήκη που
 * ξεχασμένα αναφέρεται ακόμα στον μετρητή του εξωτερικού βρόχου). Αυτός ο
 * έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (σαν συντακτικό σφάλμα) — ίδια λογική
 * με το `findUndeclaredGlossaReference` — ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ
 * αν βρίσκεται σε κλάδο/βρόχο που δεν θα εκτελούνταν σε αυτό το
 * συγκεκριμένο τρέξιμο. Ισχύει ΚΑΙ για ΓΛΩΣΣΑ ΚΑΙ για Ψευδογλώσσα (δεν
 * είναι ζήτημα δηλώσεων, είναι γενικός κανόνας ορθότητας βρόχων).
 */
export interface ReusedLoopCounter {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

interface LoopCounter {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

/**
 * Εξάγει το όνομα του μετρητή μιας εντολής βρόχου — ρητό για ΓΙΑ, "μαντεμένο"
 * από τη συνθήκη για ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ ("μετρητής <=/</>/>= έκφραση", ο
 * μετρητής ΠΑΝΤΑ αριστερά — ίδιο μοτίβο με `Interpreter.tryInferLoopBoundFromCondition`,
 * αλλά εδώ ΔΕΝ χρειάζεται να αξιολογήσουμε τίποτα, μόνο το όνομα). `null`
 * όταν δεν αναγνωρίζεται καθαρά μετρητής — "απόρριψη αντί για μάντεμα".
 */
function extractLoopCounter(stmt: Statement): LoopCounter | null {
  if (stmt.kind === 'ForStatement') {
    return { name: stmt.variable.name, line: stmt.variable.line, column: stmt.variable.column };
  }
  if (stmt.kind === 'WhileStatement' || stmt.kind === 'RepeatUntilStatement') {
    const condition = stmt.condition;
    if (
      condition.kind === 'BinaryExpression' &&
      condition.left.kind === 'Identifier' &&
      (condition.operator === '<=' || condition.operator === '<' || condition.operator === '>' || condition.operator === '>=')
    ) {
      return { name: condition.left.name, line: condition.left.line, column: condition.left.column };
    }
  }
  return null;
}

function checkStatement(stmt: Statement, active: ReadonlySet<string>): ReusedLoopCounter | null {
  switch (stmt.kind) {
    case 'ForStatement':
    case 'WhileStatement':
    case 'RepeatUntilStatement': {
      const counter = extractLoopCounter(stmt);
      if (counter === null) return checkStatements(stmt.body, active);
      if (active.has(counter.name)) return counter;
      return checkStatements(stmt.body, new Set([...active, counter.name]));
    }
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, active);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, active);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, active);
      return null;
    }
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, active);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, active);
      return null;
    }
    case 'AssignmentStatement':
    case 'ReadStatement':
    case 'WriteStatement':
    case 'SwapStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], active: ReadonlySet<string>): ReusedLoopCounter | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, active);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ επαναχρησιμοποίηση μετρητή βρόχου μέσα σε εμφωλευμένο
 * βρόχο (με σειρά εμφάνισης στον κώδικα), ή `null` αν δεν υπάρχει καμία.
 * Δουλεύει πάνω σε ΟΠΟΙΑΔΗΠΟΤΕ λίστα εντολών — κύριο σώμα προγράμματος,
 * "τμήμα αλγορίθμου", ή σώμα ενός υποπρογράμματος (ο καλών πρέπει να τα
 * ελέγξει ΞΕΧΩΡΙΣΤΑ, το καθένα με τη ΔΙΚΗ του αρχική κενή "ενεργή" λίστα,
 * αφού κάθε υποπρόγραμμα έχει αυστηρά τοπική εμβέλεια).
 */
export function findReusedLoopCounter(statements: readonly Statement[]): ReusedLoopCounter | null {
  return checkStatements(statements, new Set());
}
